import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { createReissueRequest } from "../lib/api";
import { toast } from "sonner";

export default function ReissueModal({ product, open, onClose, onSubmitted }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!open || !product) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    if (!consent) {
      toast.error("Please confirm we may email you");
      return;
    }
    setBusy(true);
    try {
      await createReissueRequest({ product_id: product.id, email, phone, consent });
      setSuccess(true);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reissue-modal" className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md p-6 md:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-[#F5D565]" data-testid="reissue-modal-close">
          <X size={20} />
        </button>

        {!success ? (
          <>
            <div className="text-[11px] tracking-[0.4em] uppercase" style={{ color: "#F5D565" }}>
              Restock Waitlist
            </div>
            <h2 className="font-serif-display mt-3" style={{ fontSize: 28, color: "#FFFFFF", lineHeight: 1.15 }}>
              Currently <em className="not-italic gold-text">unavailable.</em>
            </h2>
            <p className="font-body mt-3" style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>
              Join the waitlist for restock updates. We'll email you the moment this piece is back.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <Field label="Email *" type="email" v={email} on={setEmail} testid="reissue-email" required />
              <Field label="Phone (optional)" v={phone} on={setPhone} testid="reissue-phone" />
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="accent-[#F5D565] mt-1"
                  data-testid="reissue-consent"
                />
                <span className="font-body text-[13px]" style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
                  Email me if this becomes available
                </span>
              </label>
              <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-50" data-testid="reissue-submit">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Add me to the list"}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-2" data-testid="reissue-success">
            <div className="text-[11px] tracking-[0.4em] uppercase" style={{ color: "#F5D565" }}>
              You're on the list
            </div>
            <h2 className="font-serif-display mt-4" style={{ fontSize: 28, color: "#FFFFFF", lineHeight: 1.2 }}>
              Noted.
            </h2>
            <p className="font-body mt-3 max-w-sm mx-auto" style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
              You'll be first to know if the archives open. <span style={{ color: "#F5D565" }}>🖤</span>
            </p>
            <button onClick={onClose} className="btn-gold mt-6" data-testid="reissue-close-success">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, v, on, testid, type = "text", required = false }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={v}
      required={required}
      onChange={(e) => on(e.target.value)}
      className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 text-sm focus:border-[#F5D565] outline-none"
    />
  </label>
);
